import clsx from 'clsx';
import { Form, Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';

import SwitchField from '@components/Form/SwitchField';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import useCorporateSubscriptionAccessLevelUpdate from '@hooks/useCorporateSubscriptionAccessLevelUpdate';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { ClickTarget, ContentType, click } from '@tracking';
import { ACCESS_ANNUAL, ACCESS_PRO } from '@utils/constants';
import { isGroupUser } from '@utils/corporateSubscriptionHelpers';

import { GroupAdmin, GroupUser, UserOperationType, UserType } from '../../types';
import { ADMIN_PLACEHOLDER } from '../UserTypes/Admins';
import TableCell from './TableCell';

interface UserRowProps {
  user: GroupAdmin | GroupUser;
  onUserClick: (user: GroupAdmin | GroupUser, operation: UserOperationType) => void;
}

interface FormValues {
  pro: boolean;
}

const TABLE_CELL_BASE_STYLE = 'py-12 font-suisse-intl text-button-md pr-8';

const getTextColorClass = (id: number, userType: UserType) =>
  id === ADMIN_PLACEHOLDER.id || userType === UserType.Archived
    ? 'text-grayscale-gray'
    : 'text-black';

const UserRow = ({ user, onUserClick }: UserRowProps) => {
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const isProgrammaticChange = useRef(false);
  const { id, name, email, type } = user;
  const { mutateAsync: updateCorporateSubscriptionAccessLevel, isLoading } =
    useCorporateSubscriptionAccessLevelUpdate();
  const initialValues: FormValues = {
    pro: isGroupUser(user) ? user.accessLevel === ACCESS_PRO : false,
  };
  const { trackEvent } = useEventTracking();

  const handleAccessLevelChange = ({ pro }: FormValues) => {
    const currentAccessLevel = isGroupUser(user) && user.accessLevel;
    const newAccessLevel = pro ? ACCESS_PRO : ACCESS_ANNUAL;
    const isUserActive = isGroupUser(user) && user.type === UserType.Active;
    if (currentAccessLevel === newAccessLevel || !isUserActive) {
      return;
    }

    updateCorporateSubscriptionAccessLevel(
      {
        id: user.corporateSubscriptionId,
        corporate_subscription: {
          access_level: newAccessLevel,
        },
      },
      {
        onError: () => {
          isProgrammaticChange.current = true;
          if (formikRef.current) {
            formikRef.current.setFieldValue('pro', user.accessLevel === ACCESS_PRO, false);
          }
        },
      }
    );
    trackEvent(
      click({
        clickTarget: ClickTarget.Toggle,
        contentId: user.id,
        contentType: ContentType.User,
        value: pro ? 'Pro' : 'Standard',
      })
    );
  };

  const handleSwitchChange = (submitForm: () => void) => {
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }
    submitForm();
  };

  const handleUserClick = (user: GroupAdmin | GroupUser, operation: UserOperationType) => {
    if (user.id === ADMIN_PLACEHOLDER.id) {
      onUserClick({ name: '', email: '', type: UserType.Admin }, UserOperationType.CREATE);
    } else {
      onUserClick(user, operation);
    }
  };

  return (
    <tr
      key={id}
      className="cursor-pointer border-0 border-t border-solid border-grayscale-beige hover:bg-grayscale-beige"
      onClick={() => handleUserClick(user, UserOperationType.UPDATE)}
    >
      <TableCell
        className={clsx('max-w-[110px]', getTextColorClass(id, type), {
          'hidden sm:table-cell': type === UserType.Active,
        })}
      >
        {name}
      </TableCell>
      <TableCell className={getTextColorClass(id, type)}>{email}</TableCell>
      {type === UserType.Active && (
        <td className={clsx(TABLE_CELL_BASE_STYLE, 'text-black')}>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={handleAccessLevelChange}
            enableReinitialize
          >
            {({ submitForm }) => (
              <Form onClick={(e) => e.stopPropagation()}>
                <SwitchField
                  name="pro"
                  colorTheme={Theme.Gold}
                  width={51}
                  height={30}
                  switchClassName="!m-0 !rounded-[16px]"
                  onChangeCallBack={() => handleSwitchChange(submitForm)}
                  disabled={isLoading}
                />
              </Form>
            )}
          </Formik>
        </td>
      )}
      <td className="w-20 py-12 text-right">
        <IconArrowDown className="w-20 -rotate-90 fill-grayscale-charcoal" />
      </td>
    </tr>
  );
};

export default UserRow;
